<template>
  <BaseContent>
    <div class="[scrollbar-gutter:stable_both-edges] [background-clip:content-box]">
      <div
        class="max-h-[calc(100vh-240px)] overflow-y-auto relative"
        style="--sticky-head-h: 48px"
      >
        <div
          class="sticky top-0 z-9 h-12 flex items-center bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/70 text-lg text-[#262626] font-semibold"
        >
          Банковские продукты
        </div>

        <DataTable
          v-model:expandedRows="expandedRows"
          size="small"
          class="mortgage-table bg-white"
          selectionMode="single"
          scrollable
          :scrollHeight="'calc(100vh - 240px - 48px)'"
          :pt="{
            columnheader: 'bg-transparent',
            rowExpansion: 'bg-gray-100',
            rowExpansionCell: 'p-0',
            bodyCell: '!border-b !border-[#C4C4C4] !min-h-[47px]',
            headerCell: '!border-b !border-[#C4C4C4] !min-h-[47px]'
          }"
          :data-key="getDataKey"
          :value="programs"
          :selection="program"
          :loading="loading"
          :metaKeySelection="false"
          @row-select="onRowSelect"
          @row-click="onRowClick"
        >
          <Column
            field="title"
            header="Название"
            :style="{ width: '30%' }"
          >
            <template #body="{ data }">
              <div>{{ data.product.title }}</div>
            </template>
          </Column>

          <Column
            field="bank"
            header="Банк"
            :style="{ width: '10%' }"
          >
            <template #body="{ data }">
              <BaseBankIcon
                :bankName="data.product.bank"
                :bank-display-name="getBankDisplayName(data.product.bank)"
              />
            </template>
          </Column>

          <Column
            field="rate"
            header="Ставка"
            :style="{ width: '10%' }"
          >
            <template #body="{ data }"> от {{ data.loan_rate }}% </template>
          </Column>

          <Column
            field="payment"
            header="Ежемес. платеж"
          >
            <template #body="{ data }"> {{ data.monthly_payment.toLocaleString() }}₽ </template>
          </Column>

          <Column
            field="sum"
            header="Сумма кредита"
          >
            <template #body="{ data }"> {{ data.credit_amount.toLocaleString() }}₽ </template>
          </Column>

          <Column
            field="down_payment"
            header="ПВ"
          >
            <template #body="{ data }"> {{ data.down_payment?.toLocaleString() }}₽ </template>
          </Column>

          <Column
            field="overpayment"
            header="Переплата"
          >
            <template #body="{ data }"> {{ data.overpayment.toLocaleString() }}₽ </template>
          </Column>

          <template #empty>
            <div class="text-center">
              <span class="text-gray-900 text-base font-medium">Нет подходящих продуктов</span>
            </div>
          </template>
        </DataTable>
      </div>
    </div>
  </BaseContent>
</template>

<script setup lang="ts">
import { computed, defineModel, watch } from 'vue';
import { BaseBankIcon, BaseContent } from '@/shared';
import Column from 'primevue/column';
import DataTable, { DataTableRowClickEvent, DataTableRowSelectEvent } from 'primevue/datatable';
import { MortgageCalculateProgram } from '../../types';
import { getBankDisplayName } from '../../model';

const props = defineProps<{
  programs: MortgageCalculateProgram[] | null;
  loading?: boolean;
}>();

const akbars_dream_rate_partner_inn = defineModel<string | null>('akbars_dream_rate_partner_inn', {
  default: null
});
const akbars_dream_rate_variant = defineModel<number | null>('akbars_dream_rate_variant', { default: null });

const programId = defineModel<number | null>('programId');
const program = computed(() => {
  return props.programs?.find(program => program.product.id === programId.value) || null;
});

// const expandedRows = ref()
const expandedRows = computed(() => {
  if (programId.value) {
    return (
      props.programs
        ?.filter(program => program.product.id === programId.value)
        ?.map(product => product.product.id)
        .reduce((result, item) => {
          result[item] = true;
          return result;
        }, {} as Record<number, boolean>) || null
    );
  }

  return null;
});

watch(programId, () => {
  akbars_dream_rate_partner_inn.value = null;
  akbars_dream_rate_variant.value = null;
});

function onRowSelect(event: DataTableRowSelectEvent) {
  const data = event.data as MortgageCalculateProgram;
  programId.value = data.product.id;
}
function onRowClick(event: DataTableRowClickEvent) {
  const data = event.data as MortgageCalculateProgram;
  if (data.product.id === programId.value) {
    programId.value = null;
  }
}

function getDataKey(program: MortgageCalculateProgram) {
  return program.product.id.toString();
}
</script>

<style lang="scss">
.mortgage-table {
  .p-datatable-table {
    border-collapse: separate;
    border-spacing: 0 8px;
  }

  .p-datatable-tbody > tr,
  .p-datatable-tbody > tr.p-row-odd {
    background: transparent;
  }

  .p-datatable-tbody > tr > td {
    border: none !important;
    min-height: 47px;
  }

  .p-datatable-tbody > tr > td:first-child {
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;
    overflow: hidden;
  }
  .p-datatable-tbody > tr > td:last-child {
    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
    overflow: hidden;
  }

  .p-datatable-tbody > tr:hover > td,
  .p-datatable-tbody > tr.p-highlight > td {
    color: white;
    background: #3f3f3f;
  }
}
</style>
